import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { TeamRole } from '../../../../../../typings/TeamMember.interface';
import { withStaticProperties } from '../../../utils/withStaticProperties';
import { usePermissionAwareDisabled } from '../../contexts/TeamPermissionContext';
import StyledEllipsisOverflow from '../helpers/EllipsisOverflow';
import { getStyledUtils, StyledUtilsProps } from '../helpers/StyledUtils';
import Icon, { IconName, StyledIcon } from './Icon';
import Text from './Text';
import Divider from './Divider';
interface Props extends StyledUtilsProps {
  label: React.ReactNode;
  label_icon?: IconName;
  value?: string;
  title?: string;
  icon?: 'add' | 'close';
  mono?: boolean;
  disabled?: boolean;
  active?: boolean;
  dashed?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  onIconClick?: (e: React.MouseEvent) => void;
  to?: string | Partial<Location> | ((location: Location) => Partial<Location> | string);
  hide_chevron?: boolean;
}
const StyledFilterChip = styled.button<Omit<Props, 'label' | 'label_icon' | 'icon' | 'onClick'>>(({
  theme,
  mono,
  disabled,
  active,
  dashed
}) => css`
    ${getStyledUtils}
    height: ${theme.pxToRem(20)};
    position: relative;
    display: inline-flex;
    flex-direction: row;
    align-items: center;
    font-size: ${theme.pxToRem(theme.font_sizes.xs.font_size)};
    line-height: ${theme.pxToRem(theme.font_sizes.xs.line_height)};
    background-color: ${theme.colors.surface.base.surface};
    border-radius: ${theme.radius.small};
    border: none;
    padding: 0;
    cursor: ${disabled ? 'not-allowed' : 'pointer'};
    width: fit-content;
    max-width: 100%;
    overflow: hidden;

    ${mono && css`
      font-family:
        JetBrains Mono,
        monospace;
      font-size: ${theme.pxToRem(theme.font_sizes.xs.font_size)};
      line-height: ${theme.pxToRem(theme.font_sizes.xs.line_height)};
      font-weight: ${theme.font_weights.medium};
    `}

    a, a > div {
      text-decoration: none;
      max-width: 100%;
      font-size: ${theme.pxToRem(theme.font_sizes[mono ? 'xs' : 's'].font_size)};
      line-height: ${theme.pxToRem(theme.font_sizes[mono ? 'xs' : 's'].line_height)};
      color: ${theme.colors.on.neutral.primary_neutral};
    }

    ${disabled && css`
      background-color: ${theme.colors.surface.base.disabled};
      cursor: not-allowed;

      ${StyledIcon} {
        cursor: not-allowed !important;
      }
    `}

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border: 1px ${dashed ? 'dashed' : 'solid'} ${theme.colors.outline.neutral};
      border-radius: ${theme.radius.small};
      pointer-events: none;

      ${(active || disabled) && !dashed && css`
        border: 1px solid ${theme.colors.outline.hover.neutral};
      `}
    }

    ${active && css`
      background-color: ${theme.colors.surface.base.hover.neutral};
    `}

    span:not(:first-child) {
      border-left: 1px solid ${theme.colors.outline.neutral};
    }

    &:hover:not(:disabled) {
      background-color: ${theme.colors.surface.base.hover.neutral};
      &::before {
        border-color: ${theme.colors.outline.hover.neutral};
      }
      span:not(:first-child) {
        border-color: ${theme.colors.outline.hover.neutral};
      }
    }
  `);
const StyledSplitChipContainer = styled.div<Pick<Props, 'mono' | 'disabled' | 'dashed'>>(({
  theme,
  mono,
  disabled,
  dashed
}) => css`
    ${getStyledUtils}
    height: ${theme.pxToRem(20)};
    position: relative;
    display: inline-flex;
    flex-direction: row;
    align-items: center;
    font-size: ${theme.pxToRem(theme.font_sizes.xs.font_size)};
    line-height: ${theme.pxToRem(theme.font_sizes.xs.line_height)};
    border-radius: ${theme.radius.small};
    width: fit-content;
    max-width: 100%;
    overflow: hidden;

    ${mono && css`
      font-family:
        JetBrains Mono,
        monospace;
      font-size: ${theme.pxToRem(theme.font_sizes.xs.font_size)};
      line-height: ${theme.pxToRem(theme.font_sizes.xs.line_height)};
      font-weight: ${theme.font_weights.medium};
    `}

    ${disabled && css`
      ${StyledIcon} {
        cursor: not-allowed !important;
      }
    `}

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border: 1px ${dashed ? 'dashed' : 'solid'} ${theme.colors.outline.neutral};
      border-radius: ${theme.radius.small};
      pointer-events: none;
      z-index: 1;
    }

    ${disabled && css`
      &::before {
        border: 1px solid ${theme.colors.outline.hover.neutral};
      }
    `}
  `);
const StyledChipPart = styled.button<Pick<Props, 'disabled' | 'active'> & {
  is_icon_part?: boolean;
}>(({
  theme,
  disabled,
  is_icon_part,
  active
}) => css`
    height: 100%;
    display: flex;
    align-items: center;
    background-color: ${disabled ? theme.colors.surface.base.disabled : theme.colors.surface.base.surface};
    border: none;
    padding: 0;
    cursor: ${disabled ? 'not-allowed' : 'pointer'};
    position: relative;

    ${is_icon_part && css`
      border-left: 1px solid ${theme.colors.outline.neutral};
    `}

    div {
      white-space: nowrap;
    }

    ${active && !is_icon_part && css`
      background-color: ${theme.colors.surface.base.hover.neutral};
    `}

    &:hover:not(:disabled) {
      background-color: ${theme.colors.surface.base.hover.neutral};

      ${is_icon_part && css`
        border-left-color: ${theme.colors.outline.hover.neutral};
      `}
    }

    &:focus-visible {
      outline: 2px solid ${theme.colors.outline.focus.primary};
      outline-offset: 2px;
      z-index: 2;
    }
  `);
const FilterChipComponent: React.FC<Props> = t0 => {
  const $ = _c(77);
  let disabled;
  let hide_chevron;
  let icon;
  let label;
  let label_icon;
  let onClick;
  let onIconClick;
  let props;
  let title;
  let to;
  let value;
  if ($[0] !== t0) {
    ({
      onClick,
      onIconClick,
      label_icon,
      label,
      icon,
      to,
      title,
      disabled,
      value,
      hide_chevron,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = disabled;
    $[2] = hide_chevron;
    $[3] = icon;
    $[4] = label;
    $[5] = label_icon;
    $[6] = onClick;
    $[7] = onIconClick;
    $[8] = props;
    $[9] = title;
    $[10] = to;
    $[11] = value;
  } else {
    disabled = $[1];
    hide_chevron = $[2];
    icon = $[3];
    label = $[4];
    label_icon = $[5];
    onClick = $[6];
    onIconClick = $[7];
    props = $[8];
    title = $[9];
    to = $[10];
    value = $[11];
  }
  const useSplitLayout = icon && onIconClick;
  let t1;
  let t2;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      x: 1
    };
    t2 = {
      align: "center"
    };
    $[12] = t1;
    $[13] = t2;
  } else {
    t1 = $[12];
    t2 = $[13];
  }
  let t3;
  if ($[14] !== disabled || $[15] !== label_icon) {
    t3 = label_icon && <Icon icon={label_icon} left={1} pointer={true} muted={!disabled} disabled={disabled} />;
    $[14] = disabled;
    $[15] = label_icon;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  let t4;
  if ($[17] !== label) {
    t4 = <StyledEllipsisOverflow>{label}</StyledEllipsisOverflow>;
    $[17] = label;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== props.dashed || $[20] !== t3 || $[21] !== t4 || $[22] !== title) {
    t5 = <Text size="xs" title={title} semi={true} muted={props.dashed} ellipsis={true} p={t1} flex={t2}>{t3}{t4}</Text>;
    $[19] = props.dashed;
    $[20] = t3;
    $[21] = t4;
    $[22] = title;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  const labelContent = t5;
  let t6;
  if ($[24] !== disabled || $[25] !== icon) {
    t6 = icon && <Icon icon={icon} p={0.5} pointer={true} muted={!disabled} disabled={disabled} />;
    $[24] = disabled;
    $[25] = icon;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  const iconContent = t6;
  if (useSplitLayout) {
    let t7;
    if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <Divider vertical={true} m={{
        r: 1,
        t: 1
      }} h={{
        px: 12
      }} />;
      $[27] = t7;
    } else {
      t7 = $[27];
    }
    const t8 = !!value;
    const t9 = !value;
    let t10;
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = {
        r: 1
      };
      $[28] = t10;
    } else {
      t10 = $[28];
    }
    const t11 = value || "None selected";
    let t12;
    if ($[29] !== t11 || $[30] !== t8 || $[31] !== t9) {
      t12 = <Text size="xs" semi={true} primary={t8} muted={t9} m={t10}>{t11}</Text>;
      $[29] = t11;
      $[30] = t8;
      $[31] = t9;
      $[32] = t12;
    } else {
      t12 = $[32];
    }
    let t13;
    if ($[33] !== disabled || $[34] !== hide_chevron) {
      t13 = !hide_chevron && <Icon icon="chevron_down" p={{
        r: 1
      }} pointer={true} muted={!disabled} disabled={disabled} />;
      $[33] = disabled;
      $[34] = hide_chevron;
      $[35] = t13;
    } else {
      t13 = $[35];
    }
    let t14;
    if ($[36] !== disabled || $[37] !== labelContent || $[38] !== onClick || $[39] !== props.active || $[40] !== t12 || $[41] !== t13) {
      t14 = <StyledChipPart type="button" active={props.active} disabled={disabled} onClick={onClick} aria-label="chip-label">{labelContent}{t7}{t12}{t13}</StyledChipPart>;
      $[36] = disabled;
      $[37] = labelContent;
      $[38] = onClick;
      $[39] = props.active;
      $[40] = t12;
      $[41] = t13;
      $[42] = t14;
    } else {
      t14 = $[42];
    }
    let t15;
    if ($[43] !== onClick || $[44] !== onIconClick) {
      t15 = e => {
        e.stopPropagation();
        const onClickFn = onIconClick || onClick;
        onClickFn?.(e);
      };
      $[43] = onClick;
      $[44] = onIconClick;
      $[45] = t15;
    } else {
      t15 = $[45];
    }
    let t16;
    if ($[46] !== disabled || $[47] !== iconContent || $[48] !== props.active || $[49] !== t15) {
      t16 = <StyledChipPart type="button" active={props.active} disabled={disabled} is_icon_part={true} onClick={t15} aria-label="chip-action">{iconContent}</StyledChipPart>;
      $[46] = disabled;
      $[47] = iconContent;
      $[48] = props.active;
      $[49] = t15;
      $[50] = t16;
    } else {
      t16 = $[50];
    }
    let t17;
    if ($[51] !== disabled || $[52] !== props || $[53] !== t14 || $[54] !== t16) {
      t17 = <StyledSplitChipContainer {...props} disabled={disabled} dashed={props.dashed}>{t14}{t16}</StyledSplitChipContainer>;
      $[51] = disabled;
      $[52] = props;
      $[53] = t14;
      $[54] = t16;
      $[55] = t17;
    } else {
      t17 = $[55];
    }
    return t17;
  }
  let t7;
  if ($[56] !== disabled || $[57] !== icon || $[58] !== onClick || $[59] !== onIconClick || $[60] !== props.active) {
    t7 = icon && <Icon icon={icon} p={0.5} pointer={true} muted={!props.active} disabled={disabled} onClick={e_0 => {
      e_0.stopPropagation();
      const onClickFn_0 = onIconClick || onClick;
      onClickFn_0?.(e_0);
    }} />;
    $[56] = disabled;
    $[57] = icon;
    $[58] = onClick;
    $[59] = onIconClick;
    $[60] = props.active;
    $[61] = t7;
  } else {
    t7 = $[61];
  }
  let t8;
  if ($[62] !== labelContent || $[63] !== t7) {
    t8 = <>{labelContent}{t7}</>;
    $[62] = labelContent;
    $[63] = t7;
    $[64] = t8;
  } else {
    t8 = $[64];
  }
  const content = t8;
  if (to) {
    let t9;
    if ($[65] !== content || $[66] !== to) {
      t9 = <Link to={to} onClick={_temp}>{content}</Link>;
      $[65] = content;
      $[66] = to;
      $[67] = t9;
    } else {
      t9 = $[67];
    }
    let t10;
    if ($[68] !== disabled || $[69] !== props || $[70] !== t9) {
      t10 = <StyledFilterChip disabled={disabled} {...props} as={"div" as any}>{t9}</StyledFilterChip>;
      $[68] = disabled;
      $[69] = props;
      $[70] = t9;
      $[71] = t10;
    } else {
      t10 = $[71];
    }
    return t10;
  }
  let t9;
  if ($[72] !== content || $[73] !== disabled || $[74] !== onClick || $[75] !== props) {
    t9 = <StyledFilterChip type="button" onClick={onClick} dashed={props.dashed} disabled={disabled} {...props}>{content}</StyledFilterChip>;
    $[72] = content;
    $[73] = disabled;
    $[74] = onClick;
    $[75] = props;
    $[76] = t9;
  } else {
    t9 = $[76];
  }
  return t9;
};
const FilterChipPermission: React.FC<React.ComponentPropsWithRef<typeof FilterChipComponent> & {
  role?: TeamRole;
}> = t0 => {
  const $ = _c(8);
  let clickableAreaProps;
  let hide_chevron;
  let role;
  if ($[0] !== t0) {
    ({
      role,
      hide_chevron,
      ...clickableAreaProps
    } = t0);
    $[0] = t0;
    $[1] = clickableAreaProps;
    $[2] = hide_chevron;
    $[3] = role;
  } else {
    clickableAreaProps = $[1];
    hide_chevron = $[2];
    role = $[3];
  }
  const disabled = usePermissionAwareDisabled(clickableAreaProps.disabled, role);
  const t1 = hide_chevron || false;
  let t2;
  if ($[4] !== clickableAreaProps || $[5] !== disabled || $[6] !== t1) {
    t2 = <FilterChipComponent {...clickableAreaProps} disabled={disabled} hide_chevron={t1} />;
    $[4] = clickableAreaProps;
    $[5] = disabled;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export default withStaticProperties(FilterChipComponent, {
  Permission: FilterChipPermission
});
function _temp(e_1) {
  return e_1.stopPropagation();
}